import enRaw from './en.json';
import zhCNRaw from './zh-CN.json';
import arRaw from './ar.json';

export type TranslationKeys = keyof typeof enRaw;

// Compile-time checks to ensure all dictionaries implement the full set of keys
const en: Record<TranslationKeys, string> = enRaw;
const zhCN: Record<TranslationKeys, string> = zhCNRaw;
const ar: Record<TranslationKeys, string> = arRaw;

export { en, zhCN, ar };
export { BUNDLED_LOCALIZATION_HASH } from './hash';

export type SupportedLocale = 'en' | 'zh-CN' | 'ar';

export const LOCALES: Record<SupportedLocale, string> = {
  en: 'English',
  'zh-CN': '中文',
  ar: 'العربية',
};

export const RTL_LOCALES: SupportedLocale[] = ['ar'];

export function isRTL(locale: SupportedLocale): boolean {
  return RTL_LOCALES.includes(locale);
}

const dictionaries: Record<SupportedLocale, Record<string, string>> = {
  en,
  'zh-CN': zhCN,
  ar,
};

/**
 * Translate a key for a given locale.
 * Falls back to English if the key is missing in the requested locale.
 * For use in non-React contexts (e.g., backend validation messages, email templates).
 */
export function t(
  key: TranslationKeys,
  locale: SupportedLocale = 'en',
  interpolations?: Record<string, string | number>,
): string {
  const dict = dictionaries[locale] ?? dictionaries.en;
  let value = dict[key] ?? dictionaries.en[key] ?? key;

  if (interpolations) {
    for (const [placeholder, replacement] of Object.entries(interpolations)) {
      // Use a function replacement to prevent special `$` sequences from
      // being interpreted by String.prototype.replace (e.g. currency values).
      const literal = String(replacement);
      value = value.replace(
        new RegExp(`\\{${placeholder}\\}`, 'g'),
        () => literal,
      );
    }
  }

  return value;
}
